import React, { useState, useEffect, useRef } from "react";

import { formatDistanceToNow } from 'date-fns';
import { Link, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import {
  Delete,
  Visibility,
  Add,
  Refresh,
  Storage,
  CheckCircle,
  Error as ErrorIcon,
  AccessTime,
  Search as SearchIcon,
  MonetizationOn,
  CloudOutlined,
  FilterList as FilterIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Container,
  Fab,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  Chip,
  Divider,
  Alert,
  AlertTitle,
  TextField,
  InputAdornment,
} from "@mui/material";

import Cost from '../components/cost';
import Loader from '../components/loader';
import StatTiles from '../components/statTiles';
import useCluster from '../hooks/clusters';
import useProfile from '../hooks/profile';
import usePlausible from '../hooks/usePlausible';

export function Cluster() {
  const { error, loading, clusters, deleteCluster, refreshCluster } =
    useCluster();
  const navigate = useNavigate();
  const { loading: profileLoading, profile } = useProfile();
  const { enqueueSnackbar } = useSnackbar();
  const plausible = usePlausible();
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clusterToDelete, setClusterToDelete] = useState("");

  // Create refs for tracking search term and results changes
  // These MUST be defined at the top level, not conditionally
  const searchTermRef = useRef(searchTerm);
  const filteredClustersLengthRef = useRef(0);

  // Filter clusters by search term
  const filteredClusters = clusters.filter((cluster) =>
    cluster.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Track search usage when term changes
  // This MUST be called before any conditional returns
  useEffect(() => {
    // Only track if search term changes
    if (
      searchTerm.trim() &&
      (searchTerm !== searchTermRef.current ||
        filteredClusters.length !== filteredClustersLengthRef.current)
    ) {
      plausible.trackEvent("cluster_search", {
        term: searchTerm,
        resultsCount: filteredClusters.length,
      });
    }

    // Update refs
    searchTermRef.current = searchTerm;
    filteredClustersLengthRef.current = filteredClusters.length;
  }, [searchTerm, filteredClusters.length, plausible]);

  // Handle redirects and loading states
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          <AlertTitle>Failed to load clusters</AlertTitle>
          <Typography variant="body2">
            {error?.message || "Unknown error occurred"}
          </Typography>
        </Alert>
      </Container>
    );
  }

  if (!profileLoading && !profile.stripe) {
    navigate("/signup");
  }

  if (!profileLoading && !profile.aws.id) {
    navigate("/link");
  }

  if (loading || profileLoading) {
    return <Loader />;
  }

  // Format cluster status
  const chipBase = {
    height: 22,
    fontSize: "0.75rem",
    fontFamily: "'IBM Plex Mono', ui-monospace, Menlo, monospace",
    fontWeight: 500,
    borderRadius: 999,
    maxWidth: "100%",
    "& .MuiChip-label": {
      px: 1,
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    "& .MuiChip-icon": { ml: 0.75 },
  } as const;

  const getStatusChip = (cluster: any) => {
    if (cluster?.micro?.stage === "done") {
      return (
        <Chip
          icon={<CheckCircle fontSize="small" />}
          label="running"
          color="success"
          size="small"
          sx={chipBase}
        />
      );
    } else if (cluster?.micro?.failures?.length) {
      return (
        <Chip
          icon={<ErrorIcon fontSize="small" />}
          label="failed"
          color="error"
          size="small"
          sx={chipBase}
        />
      );
    }
    const stage = cluster?.micro?.stage || "initializing";
    return (
      <Chip
        icon={<AccessTime fontSize="small" />}
        label={`creating · ${stage}`}
        color="warning"
        size="small"
        sx={chipBase}
      />
    );
  };

  // Handle cluster deletion with confirmation
  const handleDeleteCluster = (clusterName: string) => {
    if (window.confirm(`Are you sure you want to delete the cluster "${clusterName}"? This action cannot be undone.`)) {
      // Track delete attempt
      plausible.trackEvent('cluster_delete_initiated', { clusterName });
      
      try {
        deleteCluster(clusterName)
          .then(() => {
            // Track successful deletion
            plausible.trackEvent('cluster_deleted', { clusterName });
          })
          .catch(err => {
            // Track failed deletion
            plausible.trackEvent('cluster_delete_failed', { 
              clusterName,
              error: err.message 
            });
            
            enqueueSnackbar(`Failed to delete cluster ${clusterName}: ${err.message}`, {
              variant: 'error'
            });
          });
      } catch (err: any) {
        // Track error in try/catch
        plausible.trackEvent("cluster_delete_error", {
          clusterName,
          error: err.message,
        });
        
        enqueueSnackbar(`Failed to delete cluster ${clusterName}: ${err.message}`, {
          variant: 'error'
        });
      }
    }
  };

  // Empty state view
  if (!clusters?.length) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
            boxShadow: "0 1px 0 rgba(16, 26, 47, 0.04), 0 1px 2px rgba(16, 26, 47, 0.06)",
            textAlign: "center",
            my: 4,
          }}
        >
          <CloudOutlined
            sx={{ fontSize: 60, color: "primary.main", mb: 2, opacity: 0.6 }}
          />
          <Typography
            variant="h4"
            sx={{
              fontFamily: "'IBM Plex Serif', ui-serif, Georgia, serif",
              fontWeight: 500,
              fontSize: "1.75rem",
              letterSpacing: "-0.02em",
              color: "text.primary",
              mb: 2,
            }}
          >
            No clusters yet
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            You don't have any clusters yet. Create your first cluster to get
            started.
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<Add />}
            onClick={() => {
              plausible.trackEvent("create_cluster_button_clicked", {
                source: "empty_state",
              });
              navigate("/cluster/new");
            }}
            sx={{
              borderRadius: 2,
              px: 4,
              backgroundColor: "primary.main",
              color: "primary.contrastText",
              "&:hover": { backgroundColor: "primary.dark" },
            }}
          >
            Create Cluster
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
          boxShadow: "0 1px 0 rgba(16, 26, 47, 0.04), 0 1px 2px rgba(16, 26, 47, 0.06)",
          mb: 4,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <Box>
            <Typography
              sx={{
                fontFamily: "'IBM Plex Mono', ui-monospace, Menlo, monospace",
                fontSize: 12,
                fontWeight: 500,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "text.secondary",
                mb: 1,
              }}
            >
              workspaces
            </Typography>
            <Typography
              variant="h4"
              sx={{
                fontFamily: "'IBM Plex Serif', ui-serif, Georgia, serif",
                fontWeight: 500,
                fontSize: { xs: "1.75rem", md: "2.25rem" },
                letterSpacing: "-0.02em",
                color: "text.primary",
                mb: 0.5,
              }}
            >
              Your clusters
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {clusters.length} cluster{clusters.length !== 1 ? "s" : ""} · auto-refreshing every 10s
            </Typography>
          </Box>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            <TextField
              placeholder="Search clusters..."
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                sx: { borderRadius: 2 },
              }}
            />
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                plausible.trackEvent("create_cluster_button_clicked", {
                  source: "header",
                });
                navigate("/cluster/new");
              }}
              sx={{
                borderRadius: 0.5,
                backgroundColor: "primary.main",
                color: "primary.contrastText",
                "&:hover": { backgroundColor: "primary.dark" },
              }}
            >
              New cluster
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Fleet KPIs */}
      <StatTiles clusters={clusters} />

      {/* Cluster Grid */}
      <Grid container spacing={3}>
        {filteredClusters.map((cluster) => (
          <Grid item xs={12} sm={6} md={4} key={cluster.name}>
            <Card
              elevation={0}
              sx={{
                height: "100%",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: "background.paper",
                transition: "box-shadow 200ms cubic-bezier(.2,.8,.2,1), border-color 200ms cubic-bezier(.2,.8,.2,1)",
                overflow: "visible",
                "&:hover": {
                  boxShadow: "0 2px 4px rgba(16, 26, 47, 0.06), 0 4px 12px rgba(16, 26, 47, 0.06)",
                  borderColor: "primary.main",
                },
              }}
            >
              <Box
                sx={{
                  p: 2,
                  cursor: "pointer",
                }}
                onClick={() => navigate(`/cluster/${cluster.name}`)}
              >
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Storage color="primary" fontSize="small" />
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {cluster.name}
                    </Typography>
                  </Stack>
                  {getStatusChip(cluster)}
                </Stack>
              </Box>

              <Divider />

              <CardContent>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Created {formatDistanceToNow(new Date(cluster.created))}{" "}
                      ago
                    </Typography>
                    {cluster.micro.stage !== "done" && (
                      <Typography variant="body2" color="text.secondary">
                        Stage: {cluster.micro.stage}
                      </Typography>
                    )}
                  </Box>

                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Tooltip title="Monthly cost estimate">
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <MonetizationOn color="primary" fontSize="small" />
                        <Typography variant="subtitle1" fontWeight="medium">
                          <Cost pennies={cluster.micro.usage} />
                        </Typography>
                      </Stack>
                    </Tooltip>

                    <Stack direction="row" spacing={1}>
                      <Tooltip title="Refresh Cluster">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            // Track refresh attempt
                            plausible.trackEvent("cluster_refresh_initiated", {
                              clusterName: cluster.name,
                            });

                            refreshCluster(cluster.name)
                              .then(() => {
                                // Track successful refresh
                                plausible.trackEvent("cluster_refreshed", {
                                  clusterName: cluster.name,
                                });
                              })
                              .catch((err) => {
                                // Track failed refresh
                                plausible.trackEvent("cluster_refresh_failed", {
                                  clusterName: cluster.name,
                                  error: err.message,
                                });
                                
                                enqueueSnackbar(`Failed to refresh cluster ${cluster.name}: ${err.message}`, {
                                  variant: 'error'
                                });
                              });
                          }}
                          sx={{ color: "primary.main" }}
                        >
                          <Refresh fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="View Cluster">
                        <IconButton
                          size="small"
                          onClick={() => {
                            plausible.trackEvent("cluster_details_view", {
                              clusterName: cluster.name,
                            });
                            navigate(`/cluster/${cluster.name}`);
                          }}
                          sx={{ color: "primary.main" }}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Delete Cluster">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteCluster(cluster.name);
                          }}
                          sx={{ color: "#d32f2f" }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Floating action button for mobile */}
      <Fab
        color="primary"
        aria-label="add new cluster"
        onClick={() => {
          plausible.trackEvent("create_cluster_button_clicked", {
            source: "fab",
          });
          navigate("/cluster/new");
        }}
        sx={{
          position: "fixed",
          bottom: 24,
          right: 24,
          display: { sm: "flex", md: "none" },
          backgroundColor: "primary.main",
          color: "primary.contrastText",
          "&:hover": { backgroundColor: "primary.dark" },
        }}
      >
        <Add />
      </Fab>
    </Container>
  );
}

export default Cluster;
